import React, { useCallback, useEffect, useState, useMemo } from "react";
import Card from "components/card";
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";
import { MdCheckCircle, MdCancel, MdOutlineError } from "react-icons/md";
import { format } from "date-fns";
import { useGlobalContext } from "context/globalContext";
import { getFaqAPI, getFilterFaqAPI } from "Api/faqAPI";
import EditOrDeleteFAQBtn from "../edit-deleteFAQ/editDeleteFAQ";
import CreateFaqBtn from "../create-faq/createFaq";
import Loader from "components/loader/Loader";
import { consoleLog } from "helpers";

const FAQTable = () => {
  const { fetchFaq, setFetchFaq } = useGlobalContext();
  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filterTableData, setFilterTableData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [totalPages, setTotalPages] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);

  const handleDebounceSearchQuery = () => {
    let timeoutId;
    return function (value) {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setSearchQuery(value);
        setPageIndex(0);
      }, 300);
    };
  };

  const handleSearchQuery = handleDebounceSearchQuery();

  const getFilterFaq = useCallback(async () => {
    if (searchQuery === "") return;
    const token = localStorage.getItem("jwtToken");
    const limit = 10;

    const response = await getFilterFaqAPI(
      { searchQuery, pageIndex, limit },
      setIsLoading,
      token
    );

    if (response) {
      setFilterTableData(response.data);
      setTotalPages(response.totalPages);
      setFetchFaq(false);
    }
  }, [searchQuery, setFetchFaq, pageIndex]);

  const getFaq = useCallback(async () => {
    if (searchQuery !== "") return;
    const token = localStorage.getItem("jwtToken");
    const limit = 10;
    consoleLog([pageIndex, "pageIndex"]);
    const response = await getFaqAPI(setIsLoading, token, {
      pageIndex,
      limit,
    });

    if (response) {
      setTableData(response?.data);
      setTotalPages(response?.pagination?.totalPages || 0);
      setFetchFaq(false);
    }
  }, [searchQuery, setFetchFaq, pageIndex]);

  useEffect(() => {
    if (fetchFaq) {
     if (searchQuery === "") {
      getFaq();
    } else {
      getFilterFaq();
    }
      setFetchFaq(false);
    }
  }, [fetchFaq, getFaq, setFetchFaq]);

  useEffect(() => {
    if (searchQuery === "") {
      getFaq();
    } else {
      getFilterFaq();
    }
  }, [searchQuery, pageIndex, getFaq, getFilterFaq]);

  const columnsData = useMemo(
    () => [
      {
        Header: "Key",
        accessor: "key",
      },
      {
        Header: "Title",
        accessor: "title",
      },
      {
        Header: "Description",
        accessor: "description",
      },
      {
        Header: "Sort Order",
        accessor: "sortOrder",
      },
      {
        Header: "Actions",
        accessor: "actions",
      },
    ],
    []
  );

  const columns = useMemo(() => columnsData, [columnsData]);

  const tableInstance = useTable(
    {
      columns,
      data: searchQuery === "" ? tableData : filterTableData,
      manualPagination: true,
      pageCount: totalPages,
      pageIndex,
      initialState: { pageIndex: 0, pageSize: 10 },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    gotoPage,
  } = tableInstance;

  const handlePageChange = (direction) => {
    if (direction === "next" && pageIndex < totalPages - 1) {
      setPageIndex((prevPageIndex) => {
        const newPageIndex = prevPageIndex + 1;
        gotoPage(newPageIndex);
        return newPageIndex;
      });
    }

    if (direction === "previous" && pageIndex > 0) {
      setPageIndex((prevPageIndex) => {
        const newPageIndex = prevPageIndex - 1;
        gotoPage(newPageIndex);
        return newPageIndex;
      });
    }
  };

  return (
    <Card extra={"w-full h-full p-4 sm:overflow-x-auto "}>
      <div className="relative flex items-center justify-between ">
        <div className="text-xl font-bold text-navy-700 dark:text-white ">
          <div className="relative w-full sm:w-64">
            <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2 text-lg text-gray-400">
              🔍
            </span>
            <input
              type="search"
              className="w-full rounded-md border border-gray-300 py-2 pl-8 pr-2 text-sm transition-all duration-300 ease-in-out hover:border-blue-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:!bg-gray-10 dark:text-white"
              placeholder="Search"
              onChange={(e) => {
                e.preventDefault();
                handleSearchQuery(e.target.value);
              }}
            />
          </div>
        </div>
        <CreateFaqBtn />
      </div>

      <div className="mt-4 h-full overflow-x-scroll xl:overflow-auto">
        {isLoading && tableData.length === 0 && (
          <div className="flex h-[400px] w-full items-center justify-center">
            <Loader />
          </div>
        )}
        {!isLoading &&
          ((searchQuery && filterTableData.length === 0) ||
            (!searchQuery && tableData.length === 0)) && (
            <span className="block w-full text-center"> No data found.</span>
          )}
        {(searchQuery ? filterTableData : tableData).length > 0 && (
          <table {...getTableProps()} className="w-full">
            <thead>
              {headerGroups.map((headerGroup) => (
                <tr
                  {...headerGroup.getHeaderGroupProps()}
                  key={headerGroup.getHeaderGroupProps().key}
                >
                  {headerGroup.headers.map((column) => {
                    return (
                      <th
                        key={column.id}
                        className="border-b border-gray-200 pr-20 pb-[10px] text-start dark:!border-navy-700"
                      >
                        <p className="text-xs tracking-wide text-gray-600">
                          {column.render("Header")}
                        </p>
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()} key={row.original.key}>
                    {row.cells.map((cell) => {
                      let data = cell.render("Cell");

                      if (cell.column.Header === "Status") {
                        let statusIcon;
                        if (cell.value === true) {
                          statusIcon = (
                            <MdCheckCircle className="text-green-500" />
                          );
                        } else if (cell.value === false) {
                          statusIcon = <MdCancel className="text-red-500" />;
                        } else if (cell.value === "Error") {
                          statusIcon = (
                            <MdOutlineError className="text-orange-500" />
                          );
                        }
                        data = (
                          <div className="flex items-center">
                            <div className="rounded-full text-xl">
                              {statusIcon}
                            </div>
                          </div>
                        );
                      } else if (cell.column.Header === "Value") {
                        data = (
                          <div className="scrollbar-custom mr-5 max-h-[150px] overflow-auto overflow-y-auto overflow-x-hidden pt-[4px] pb-[8px] sm:text-[14px]">
                            {cell.value}
                          </div>
                        );
                      } else if (cell.column.Header === "Description") {
                        data = (
                          <div className="scrollbar-custom max-h-[150px] overflow-y-auto overflow-x-hidden pt-[4px] pb-[8px] sm:text-[14px]">
                            {cell.value}
                          </div>
                        );
                      } else if (cell.column.Header === "Created At") {
                        data = format(
                          new Date(cell?.value || null),
                          "yyyy-MM-dd HH:mm:ss"
                        );
                      } else if (cell.column.Header === "Actions") {
                        data = (
                          <EditOrDeleteFAQBtn
                            width="w-[68px]"
                            value={cell.value}
                            cell={cell}
                          />
                        );
                      }

                      return (
                        <td
                          className="w-[100px] px-[7px] pt-[14px] pb-[8px] sm:text-[14px]"
                          {...cell.getCellProps()}
                          key={cell.column.id}
                        >
                          <p
                            className={`max-w-fit ${
                              cell.column.Header === "Key" && "cursor-pointer"
                            }`}
                          >
                            {data}
                          </p>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {((!!tableData.length && !searchQuery) ||
          (!!filterTableData.length && searchQuery)) && (
          <div className="mt-4 flex items-center justify-between">
            <button
              onClick={() => handlePageChange("previous")}
              disabled={pageIndex === 0}
              className={`rounded-md px-4 py-2 text-sm font-medium text-white ${
                pageIndex > 0
                  ? "bg-blue-500 hover:bg-blue-600"
                  : "cursor-not-allowed bg-gray-300"
              }`}
            >
              Previous
            </button>

            <span className="text-sm text-gray-700 dark:text-white">
              Page <strong>{pageIndex + 1}</strong> of{" "}
              <strong>{totalPages}</strong>
            </span>

            <button
              onClick={() => handlePageChange("next")}
              disabled={pageIndex >= totalPages - 1}
              className={`rounded-md px-4 py-2 text-sm font-medium text-white ${
                pageIndex < totalPages - 1
                  ? "bg-blue-500 hover:bg-blue-600"
                  : "cursor-not-allowed bg-gray-300"
              }`}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </Card>
  );
};

export default FAQTable;
